import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
  Injector,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { IdName } from 'src/app/shared/models/id-name';
import { ITenantUserAccess } from 'src/app/shared/user-view/models/tenant-user-access.model';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import { TenantSelectorComponent } from 'src/app/tenant-selector/tenant-selector.component';
import { AppUpdateService } from 'src/app/services/app-update.service';
import { TextControlService } from 'src/app/shared/text-control-module/text-control.service';
import { environment } from 'src/environments/environment';
import { ModuleName } from 'src/app/shared/enums/enums';
import { LoadTenantUserAccess } from '../../../tenant-selector/state/tenant-user-access.actions';
import { MenuListState } from '../../state/menu-list.state';
import { UiState } from '../../state/ui.state';
import { getAdminRoutesWithChildren } from '../menu-list/menu-list-routes';
import { TenantIdListComponent } from '../tenant-id-list/tenant-id-list.component';
import { ToastService } from '../../services/toast.service';
import { Animations, AnimationDurations } from '../../angular-animations/angular-animations';
import { DepartmentSelectorMobileComponent } from '../department-selector-mobile/department-selector-mobile.component';
import { UserProfileDropdownMobileComponent } from '../user-profile-dropdown-mobile/user-profile-dropdown-mobile.component';
import {
  RoutesEnum,
  UserDocumentsRoutes,
  UserNonConformancesRoutes,
  UserOrderRoutes,
} from '../../enums/routes.enum';
import { SidenavService } from '../../services/side-nav.service';
import { ToggleToolbar } from '../../state/ui.actions';

const USER_ROUTES_WITH_INNER_NAV = [
  RoutesEnum.Certificate,
  RoutesEnum.Documents,
  RoutesEnum.Maintenance,
  RoutesEnum.Storage,
  RoutesEnum.Medicines,
  RoutesEnum.Drills,
  RoutesEnum.Checklists,
  RoutesEnum.RiskAssessments,
  RoutesEnum.Users,
];
const USER_MODULES_WITH_INNER_MENU = [ModuleName.UserUSER];

const URLS_WITH_HIDDEN_OVERFLOW_ON_TOOLBAR_ANIMATION: string[] = [
  UserNonConformancesRoutes.Open,
  UserNonConformancesRoutes.Completed,
  UserNonConformancesRoutes.Closed,
  UserNonConformancesRoutes.Deleted,
  UserDocumentsRoutes.Published,
  UserDocumentsRoutes.Draft,
  UserDocumentsRoutes.ForApproval,
  UserDocumentsRoutes.Rejected,
  UserDocumentsRoutes.Disabled,
  UserDocumentsRoutes.Deleted,
  UserDocumentsRoutes.UnderRevision,
  UserDocumentsRoutes.ChangeRequests,
  UserOrderRoutes.Dashboard,
  UserOrderRoutes.Draft,
  UserOrderRoutes.ForApproval,
  UserOrderRoutes.Approved,
  UserOrderRoutes.SentToSupplier,
  UserOrderRoutes.ReceivedPartially,
  UserOrderRoutes.Received,
  UserOrderRoutes.Cancelled,
];

@Component({
  selector: 'app-main-nav',
  templateUrl: './main-nav.component.html',
  styleUrls: ['./main-nav.component.scss'],
  animations: [Animations.halfRotation, Animations.slideDown],
  standalone: false,
})
export class MainNavComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly injector = inject(Injector);

  @ViewChild('innerNavToggleButton', { read: ElementRef }) innerNavToggleButton: ElementRef;
  @ViewChild('innerNavToggleButtonForMobile', { read: ElementRef })
  innerNavToggleButtonForMobile: ElementRef;
  @ViewChild('innerNavContent', { read: ElementRef }) innerNavContent: ElementRef;
  @ViewChild('drawer', { read: ElementRef }) mainSidenavEl: ElementRef;
  @ViewChild('drawer', { read: MatSidenav }) mainSidenav: MatSidenav;
  @ViewChild('inner', { read: MatSidenav }) innerMenu: MatSidenav;

  adminInnerModuleName = select(MenuListState.getAdminInnerNavModuleName);
  isDarkMode = select(UiState.isDarkMode);
  currentDepartmentId = select(TenantUserAccessState.currentDepartmentId);
  private readonly currentDepartmentChanges$ = toObservable(this.currentDepartmentId, {
    injector: this.injector,
  });

  readonly isHandset = toSignal(
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      map((result: BreakpointState) => result.matches),
      shareReplay({ bufferSize: 1, refCount: true })
    ),
    { initialValue: false }
  );

  selectedDepartment = select(TenantUserAccessState.currentDepartment);
  allAvailableDepartments = select(TenantUserAccessState.getAllAvailableDepartments);
  sortedDepartments = computed(() =>
    [...(this.allAvailableDepartments() ?? [])].sort((a, b) => a.name.localeCompare(b.name))
  );
  userAccess = select(TenantUserAccessState.userAccess);
  isToolbarCollapsed = select(UiState.isToolbarCollapsed);

  readonly ALL_DEPS_TOKEN = ALL_DEPS_TOKEN;
  allDepsIconPath = '/assets/icons/all-departments.svg';
  departmentPlaceHolderIconPath = '/assets/icons/department-placeholder.svg';
  innerMenuOpened = false;
  showSecondarySidenav = false;
  isInAdmin: boolean;
  depId: string;
  desktopMode: boolean;
  url = environment.textControlWS;
  forceReload = false;
  sidenavOpen$!: Observable<boolean>;
  isToolbarAnimating = false;

  hideScrollOnToolbarAnimationInModule = false;

  get useTextControl(): boolean {
    return TextControlService.isTextControlEnabled;
  }

  private initialMainNavWidth = 0;
  private firstRun = true;
  private subscription = new Subscription();
  private currentModule?: string;
  private toolbarAnimationTimeout?: number;

  constructor(
    public textControl: TextControlService,
    private appUpdateService: AppUpdateService,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private store: Store,
    private translate: TranslateService,
    private renderer: Renderer2,
    private toastService: ToastService,
    private sidenavService: SidenavService
  ) {}

  ngOnInit(): void {
    this.listenToRouteChanges();

    this.subscription.add(
      this.breakpointObserver
        .observe(['(min-width: 800px)'])
        .subscribe((state: BreakpointState) => {
          this.desktopMode = state.matches;
          if (this.firstRun) {
            this.getSelectors();
            this.firstRun = false;
          }
        })
    );

    this.subscription.add(
      this.currentDepartmentChanges$.subscribe((depId: string) => {
        this.depId = depId;
      })
    );

    this.subscription.add(
      this.textControl.forceReload.subscribe(() => {
        this.forceReload = true;
        setTimeout(() => {
          this.forceReload = false;
        }, 100);
      })
    );
  }

  ngAfterViewInit(): void {
    this.bindInnerNavToggleButton();
    this.initialMainNavWidth = this.mainSidenavEl.nativeElement.getBoundingClientRect().width;
    this.sidenavService.setSidenav(this.mainSidenav);
  }

  ngOnDestroy(): void {
    if (this.toolbarAnimationTimeout) {
      clearTimeout(this.toolbarAnimationTimeout);
    }
    this.subscription.unsubscribe();
    this.textControl.dispose();
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.textControl.dispose();
  }

  bindInnerNavToggleButton(): void {
    const callback: MutationCallback = (mutationsList: MutationRecord[]) => {
      mutationsList.forEach(({ type }) => {
        if (type === 'attributes') {
          if (this.desktopMode) {
            this.setSecondarySidenavButtonOffset();
          } else {
            this.setInnerNavForMobileOffset();
          }
        }
      });
    };

    const observer = new MutationObserver(callback);
    observer.observe(this.innerNavContent.nativeElement, { attributes: true });
  }

  toggleMainNav(): void {
    this.mainSidenav.toggle();
    if (this.desktopMode) {
      this.setSecondarySidenavButtonOffset();
    }
  }

  setSecondarySidenavButtonOffset(): void {
    const baseOffset = this.mainSidenav.opened ? this.initialMainNavWidth : 0;
    const newContentOffset = this.innerNavContent.nativeElement.style.marginLeft;
    let newButtonOffset = baseOffset;
    if (newContentOffset) {
      newButtonOffset += +newContentOffset.replace('px', '');
    }
    this.renderer.setStyle(
      this.innerNavToggleButton.nativeElement,
      'margin-left',
      `${newButtonOffset}px`
    );
  }

  setInnerNavForMobileOffset(): void {
    const baseOffset = this.mainSidenav.opened ? this.initialMainNavWidth : 0;
    const newContentOffset = this.innerNavContent.nativeElement.style.marginLeft;
    let newButtonOffset = baseOffset;
    if (newContentOffset) {
      newButtonOffset += +newContentOffset.replace('px', '');
    }
    this.renderer.setStyle(
      this.innerNavToggleButtonForMobile.nativeElement,
      'margin-left',
      `${newButtonOffset}px`
    );
  }

  openInnerMenu(open?: boolean, url?: string, isAdmin?: boolean): void {
    if (open !== undefined && !this.isInCurrentModule(url, isAdmin)) {
      this.innerMenuOpened = open;
    } else if (!this.isInCurrentModule(url, isAdmin)) {
      this.toggleInnerMenu();
    }
  }

  toggleInnerMenu(): void {
    this.innerMenuOpened = !this.innerMenuOpened;
  }

  toggleToolbar(): void {
    if (this.toolbarAnimationTimeout) {
      clearTimeout(this.toolbarAnimationTimeout);
    }
    // Block overflow while animating to avoid horizontal scrollbar
    this.isToolbarAnimating = true;
    this.store.dispatch(new ToggleToolbar(!this.store.selectSnapshot(UiState.isToolbarCollapsed)));
    this.toolbarAnimationTimeout = setTimeout(() => {
      this.isToolbarAnimating = false;
    }, AnimationDurations.TOOLBAR_SLIDE + 100) as unknown as number;
  }

  goToCCOMDocs(): void {
    window.open('https://support.ccom.no/portal/en/kb/ccom', '_blank');
  }

  openDepartmentSelectorMobile(): void {
    const dialogRef = this.dialog.open(DepartmentSelectorMobileComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      const tenantId = result.data;
      if (tenantId !== getTenantId()) {
        this.store.dispatch(new LoadTenantUserAccess(tenantId));
      }
    });
  }

  openUserProfileMobileDialog(): void {
    this.dialog.open(UserProfileDropdownMobileComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      autoFocus: false,
    });
  }

  private listenToRouteChanges(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.processURL(event.url);
      });
  }

  private isInCurrentModule(url?: string, isAdmin?: boolean): boolean {
    const currentModule = isAdmin ? url?.split('/')[2] : url?.split('/')[1];
    this.hideScrollOnToolbarAnimationInModule = URLS_WITH_HIDDEN_OVERFLOW_ON_TOOLBAR_ANIMATION.some(
      (_url) => this.router.url?.endsWith(_url)
    );
    if (currentModule !== this.currentModule) {
      this.currentModule = currentModule;
      return false;
    }
    return true;
  }

  private processURL(url: string): void {
    this.isInAdmin = url.split('/').some((fragment) => fragment === 'admin');
    if (this.isInAdmin) {
      this.showSecondarySidenav = getAdminRoutesWithChildren().some((r) => url.includes(r));
    } else {
      // this is very bad, if a url includes documents, even though it's sth else entirely, it treats it as a document module
      this.showSecondarySidenav = USER_ROUTES_WITH_INNER_NAV.some((moduleName) =>
        url.includes(moduleName.toLowerCase())
      );
    }
    this.openInnerMenu(this.showSecondarySidenav, url, this.isInAdmin);
  }

  private getSelectors(): void {
    if (!this.appUpdateService.isPopupShown) {
      this.subscription.add(
        this.store
          .select(TenantUserAccessState.getAllAvailableTenants)
          .pipe(take(1))
          .subscribe((tenants: ITenantUserAccess[]) => {
            if (tenants && tenants.length > 1) {
              const selectedTenantId = TenantUserAccessState.getLastSelectedTenantId();
              if (!selectedTenantId) {
                this.dialog
                  .open(TenantSelectorComponent, {
                    width: this.desktopMode ? '50rem' : '100vw',
                    height: this.desktopMode ? '' : '100vh',
                    maxWidth: this.desktopMode ? '80vw' : '100vw',
                    autoFocus: false,
                  })
                  .afterClosed()
                  .subscribe();
              }
            } else if (tenants && tenants.length === 0) {
              this.toastService.error('userHasNoActiveTenants');
            }
          })
      );
    }
  }

  private openDialog(tenants: IdName[]): void {
    const title = this.translate.instant('chooseATenant');

    const dialogRef = this.dialog.open(TenantIdListComponent, {
      width: '250px',
      data: { title, tenants },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      const tenantId = result.data;
      if (tenantId !== getTenantId()) {
        this.store.dispatch(new LoadTenantUserAccess(tenantId));
      }
    });
  }
}
